// API client para apps/invoicing/ (Django backend NABU-61).
// Endpoints admin (Bearer JWT) + lookup público por public_token.

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  DashboardSnapshot,
  Invoice,
  InvoiceCreateIn,
  InvoiceListFilters,
  InvoiceUpdateIn,
  InvoicingProfile,
  InvoicingProfileUpdateIn,
  Payment,
  PaymentCreateIn,
} from './types';

const DJANGO_PROXY = '/django-api';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  const orgId = localStorage.getItem('org_id') || '';
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (orgId) headers['X-Org-Id'] = orgId;
  return headers;
}

export class InvoicingApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'InvoicingApiError';
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    ...init,
    headers: { ...headers, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: string }).detail || `HTTP ${res.status}`;
    throw new InvoicingApiError(
      typeof detail === 'string' ? detail : JSON.stringify(detail),
      res.status,
    );
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// Para endpoints públicos no se mandan auth headers (auth=None en backend).
async function publicRequest<T>(path: string): Promise<T> {
  const res = await fetch(`${DJANGO_PROXY}${path}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = (body as { detail?: string }).detail || `HTTP ${res.status}`;
    throw new InvoicingApiError(
      typeof detail === 'string' ? detail : JSON.stringify(detail),
      res.status,
    );
  }
  return res.json();
}

const ADMIN = '/api/v1/admin/invoicing';
const PUBLIC = '/api/v1/invoicing';

/**
 * Normaliza respuestas de lista.
 *
 * El router declara `response=list[InvoiceOut]`, pero Django Ninja lo pagina
 * y lo entrega envuelto: `{ items: [...], count: N }`. El front hacía
 * `data.map(...)` sobre ese objeto → "r.map is not a function" y la vista
 * de Facturas tronaba entera.
 *
 * Acepta las DOS formas a propósito: así no se vuelve a romper si un
 * endpoint gana o pierde paginación más adelante.
 */
function unwrapList<T>(res: unknown): T[] {
  if (Array.isArray(res)) return res as T[];
  if (res && typeof res === 'object' && Array.isArray((res as { items?: unknown }).items)) {
    return (res as { items: T[] }).items;
  }
  return [];
}

export const invoicingApi = {
  // ── Invoices ─────────────────────────────────────────────
  list: (filters: InvoiceListFilters = {}): Promise<Invoice[]> => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.lead_id) params.set('lead_id', filters.lead_id);
    if (filters.limit !== undefined) params.set('limit', String(filters.limit));
    if (filters.offset !== undefined) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return request<unknown>(`${ADMIN}/invoices${qs ? `?${qs}` : ''}`)
      .then((res) => unwrapList<Invoice>(res));
  },

  get: (id: string): Promise<Invoice> => request<Invoice>(`${ADMIN}/invoices/${id}`),

  create: (data: InvoiceCreateIn): Promise<Invoice> =>
    request<Invoice>(`${ADMIN}/invoices`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  createFromQuote: (quoteId: string): Promise<Invoice> =>
    request<Invoice>(`${ADMIN}/invoices/from-quote/${quoteId}`, { method: 'POST' }),

  update: (id: string, data: InvoiceUpdateIn): Promise<Invoice> =>
    request<Invoice>(`${ADMIN}/invoices/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string): Promise<void> =>
    request<void>(`${ADMIN}/invoices/${id}`, { method: 'DELETE' }),

  // ── Payments ─────────────────────────────────────────────
  listPayments: (invoiceId: string): Promise<Payment[]> =>
    request<unknown>(`${ADMIN}/invoices/${invoiceId}/payments`)
      .then((res) => unwrapList<Payment>(res)),

  createPayment: (invoiceId: string, data: PaymentCreateIn): Promise<Payment> =>
    request<Payment>(`${ADMIN}/invoices/${invoiceId}/payments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  deletePayment: (invoiceId: string, paymentId: string): Promise<void> =>
    request<void>(`${ADMIN}/invoices/${invoiceId}/payments/${paymentId}`, {
      method: 'DELETE',
    }),

  // ── Dashboard ────────────────────────────────────────────
  dashboard: (): Promise<DashboardSnapshot> =>
    request<DashboardSnapshot>(`${ADMIN}/dashboard`),

  // ── Profile ──────────────────────────────────────────────
  getProfile: (): Promise<InvoicingProfile> =>
    request<InvoicingProfile>(`${ADMIN}/profile`),

  updateProfile: (data: InvoicingProfileUpdateIn): Promise<InvoicingProfile> =>
    request<InvoicingProfile>(`${ADMIN}/profile`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  // ── Public (sin auth) ────────────────────────────────────
  publicView: (publicToken: string) =>
    publicRequest<{
      number: string;
      status: string;
      customer_name: string;
      customer_tax_id: string;
      issue_date: string;
      due_date: string;
      currency: string;
      subtotal: string;
      tax: string;
      retention: string;
      total: string;
      paid_amount: string;
      balance_due: string;
      line_items: Invoice['line_items'];
      bank_info: string;
      footer_notes: string;
    }>(`${PUBLIC}/public/${publicToken}`),

  publicPdfUrl: (publicToken: string): string =>
    `${DJANGO_PROXY}${PUBLIC}/public/${publicToken}/pdf`,
};
